import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogOut, Copy, Users, DollarSign, TrendingUp, Calendar, Check, Wallet, AlertTriangle, AlertCircle, CreditCard, PauseCircle, XCircle, Award, User, Calculator, MessageCircle, Building2, X, Megaphone, Lock, Sparkles, Crown, GitBranch, CheckCircle, Upload, BookOpen, Briefcase, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import PaymentSettings from '@/components/PaymentSettings';
import EarningsSimulator from '@/components/EarningsSimulator';
import CommunityMetrics from '@/components/CommunityMetrics';
import BlogSection from '@/components/BlogSection';
import MessagesPanel from '@/components/MessagesPanel';
import AuthorVerification from '@/components/AuthorVerification';
import PaymentPlanModal from '@/components/PaymentPlanModal';
import AuthorCheckoutModal from '@/components/AuthorCheckoutModal';
import ProMarketing from '@/components/ProMarketing';
import MyImageGallery from '@/components/MyImageGallery';
import RedeemCommissions from '@/components/RedeemCommissions';
import PublicalaUploader from '@/components/PublicalaUploader';
import ViubuxPlayUploader from '@/components/ViubuxPlayUploader';
import AccountSecurity from '@/components/AccountSecurity';
import NubankInvite from '@/components/NubankInvite';
import AIChatWidget from '@/components/AIChatWidget';
import EditorialDashboard from '@/components/EditorialDashboard';
import LatestContentCarousel from '@/components/LatestContentCarousel';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '@/components/LanguageSelector';

// Helper para formatear moneda con comas
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return '$' + Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [productionCommissions, setProductionCommissions] = useState(null);
  const [poolShare, setPoolShare] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  const [badges, setBadges] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [metricsVisible, setMetricsVisible] = useState(true);
  const [payoutAvailability, setPayoutAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [authorVerificationStatus, setAuthorVerificationStatus] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAuthorCheckout, setShowAuthorCheckout] = useState(false);
  const [authorInitial, setAuthorInitial] = useState({ membership: true, subscription: true });
  const [authorOptions, setAuthorOptions] = useState(null);
  const [sendingDistributionEmail, setSendingDistributionEmail] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventFormData, setEventFormData] = useState({ date: '', description: '' });
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [showPublicalaUploader, setShowPublicalaUploader] = useState(false);
  const [showViubuxPlayUploader, setShowViubuxPlayUploader] = useState(false);

  // Set default tab based on URL param or user type
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      // URL parameter takes priority
      setActiveTab(tabFromUrl);
    } else if (user?.user_type === 'author') {
      setActiveTab('benefits');
    }
  }, [user, searchParams]);

  // Check if author is Lite tier (restricted access)
  const isAuthorLite = user?.user_type === 'author' && user?.author_tier === 'lite';
  const isAuthorPremium = user?.user_type === 'author' && user?.author_tier !== 'lite';

  useEffect(() => {
    if (user && token) {
      fetchDashboardData();
      fetchUnreadMessages();
      fetchAnnouncementCount();
      if (user.user_type === 'author') {
        fetchAuthorVerificationStatus();
        fetchAuthorOptions();
      }
    }
  }, [user, token]);

  const fetchAuthorOptions = async () => {
    try {
      const response = await axios.get(`${API}/author/checkout-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthorOptions(response.data);
    } catch (error) {
      console.error('Error fetching author checkout options:', error);
    }
  };

  const openAuthorCheckout = (focus) => {
    setAuthorInitial({ membership: focus === 'membership', subscription: focus === 'subscription' });
    setShowAuthorCheckout(true);
  };

  const fetchAuthorVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/author/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthorVerificationStatus(response.data);
    } catch (error) {
      console.error('Error fetching author verification status:', error);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessages(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    }
  };

  const fetchAnnouncementCount = async () => {
    try {
      const response = await axios.get(`${API}/blog/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncementCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error('Error fetching announcement count:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const requests = [
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/dashboard/referrals`, { headers }),
        axios.get(`${API}/dashboard/commissions`, { headers }),
        axios.get(`${API}/notifications`, { headers }),
        axios.get(`${API}/payments/my-requests`, { headers }),
        axios.get(`${API}/user/subscription-status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/user/subscription-price`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/user/badges`, { headers }).catch(() => ({ data: { badges: [], badge_details: [] } })),
        axios.get(`${API}/community/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/settings/simulator-metrics-visibility`, { headers }).catch(() => ({ data: { is_visible: true } })),
        axios.get(`${API}/payments/payout-availability`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/external/production-commissions/${user?.id}`, { headers }).catch(() => ({ data: null }))
      ];

      if (user?.user_type === 'author') {
        requests.push(axios.get(`${API}/dashboard/pool-share`, { headers }));
      }

      const responses = await Promise.all(requests);

      setStats(responses[0].data);
      setReferrals(responses[1].data);
      setCommissions(responses[2].data);
      setNotifications(responses[3].data);
      setPaymentRequests(responses[4].data);
      setSubscriptionStatus(responses[5].data);
      setPriceInfo(responses[6].data);
      setBadges(responses[7].data?.badge_details || []);
      setCommunityStats(responses[8].data);
      setMetricsVisible(responses[9].data?.is_visible !== false);
      setPayoutAvailability(responses[10].data);
      setProductionCommissions(responses[11].data);
      
      if (user?.user_type === 'author') {
        setPoolShare(responses[12].data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Check if it's a server error (5xx) or network error
      if (!error.response || error.response?.status >= 500) {
        toast.error('🔄 El sistema se está actualizando. Re-intenta en 2 minutos.');
      } else {
        toast.error('Error al cargar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de planes de pago en lugar de ir directo a checkout
  const handlePayment = async (packageType) => {
    setShowPaymentModal(true);
  };

  // Función legacy para pagos directos (por si se necesita)
  const handleDirectPayment = async (packageType) => {
    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          package_type: packageType,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Error al procesar el pago');
    }
  };

  const copyReferralLink = async () => {
    const link = `https://connect.viubux.com?ref=${user.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado al portapapeles');
    } catch (err) {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Link copiado al portapapeles');
      } catch (e) {
        toast.error('No se pudo copiar. Por favor copia manualmente: ' + link);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleRequestPayout = async () => {
    // Verificar si puede cobrar
    if (subscriptionStatus && !subscriptionStatus.can_withdraw) {
      toast.error('No puedes solicitar cobro mientras tu suscripción esté pausada o inactiva.');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/payments/request-payout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      console.error('Error requesting payout:', error);
      toast.error(error.response?.data?.detail || 'Error al solicitar pago');
    }
  };

  const handleCreateSubscription = async () => {
    setSubscriptionLoading(true);
    try {
      // Usar el endpoint que maneja descuentos automáticamente
      const response = await axios.post(
        `${API}/user/create-subscription-with-discount`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.response?.data?.detail || 'Error al crear suscripción');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handlePauseSubscription = async () => {
    if (!window.confirm('¿Estás seguro de pausar tu suscripción? No se realizarán cobros automáticos.')) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/user/pause-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      console.error('Error pausing subscription:', error);
      toast.error(error.response?.data?.detail || 'Error al pausar suscripción');
    }
  };

  // Enviar email para iniciar proceso de distribución
  const handleDistributionRequest = async () => {
    setSendingDistributionEmail(true);
    try {
      const response = await axios.post(
        `${API}/benefits/distribution-request`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Solicitud enviada correctamente. Te contactaremos pronto.');
    } catch (error) {
      console.error('Error sending distribution request:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSendingDistributionEmail(false);
    }
  };

  // Enviar solicitud de evento
  const handleEventRequest = async () => {
    if (!eventFormData.date) {
      toast.error('Por favor selecciona una fecha para tu evento');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/benefits/event-request`,
        { 
          preferred_date: eventFormData.date,
          description: eventFormData.description 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Solicitud de evento enviada. Te contactaremos para cotizar.');
      setShowEventModal(false);
      setEventFormData({ date: '', description: '' });
    } catch (error) {
      console.error('Error sending event request:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud de evento');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('¿Estás seguro de cancelar tu suscripción? Tu membresía permanecerá activa hasta la fecha de expiración.')) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/user/cancel-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.detail || 'Error al cancelar suscripción');
    }
  };

  const handleSwitchToFree = async () => {
    if (!window.confirm('¿Cambiar a plan gratuito? Conservarás tu membresía hasta el final del período actual y luego pasarás a plan gratuito (sin renovación).')) {
      return;
    }
    try {
      const response = await axios.post(
        `${API}/subscriptions/switch-to-free`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      console.error('Error switching to free:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar a plan gratuito');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/viubux_logo_web.png" 
            alt="ViubuxConnect" 
            className="h-12 mx-auto mb-4 animate-pulse"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="text-gray-600 text-xl">Cargando...</div>
        </div>
      </div>
    );
  }

  // Dashboard para Editoriales / Publishers
  if (user?.user_type === 'editorial') {
    return <EditorialDashboard />;
  }

  // Dashboard para Audio-Lectores (Estilo claro app.viubux.com)
  if (user?.user_type === 'consumer') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header - Estilo app.viubux.com */}
        <header className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-40">
          <div className="container mx-auto px-4 md:px-6 py-3 flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="/viubux_logo_web.png" 
                alt="Viubux Connect" 
                className="h-10 md:h-12 w-auto object-contain"
                style={{ maxWidth: '180px' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="gotham-bold text-xl" style="color:#272b96">ViubuxConnect</span>';
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="verdana-regular text-gray-500 hidden md:block text-sm">{user?.email}</span>
              {!isAuthorLite && (
              <Button
                data-testid="networking-btn"
                onClick={() => navigate('/networking')}
                variant="outline"
                className="border-violet-200 text-violet-600 hover:bg-violet-50"
              >
                <Users className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Networking</span>
              </Button>
            )}
              <Button
                data-testid="messages-btn"
                onClick={() => setShowMessages(true)}
                variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50 relative"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Mensajes</span>
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {unreadMessages}
                  </span>
                )}
              </Button>
              <Button
                data-testid="profile-btn"
                onClick={() => navigate('/profile')}
                variant="outline"
                className="border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <User className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Mi Perfil</span>
              </Button>
              {/* Language Selector */}
              <LanguageToggle />
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Messages Panel */}
        <MessagesPanel 
          token={token} 
          isOpen={showMessages} 
          onClose={() => {
            setShowMessages(false);
            fetchUnreadMessages();
          }} 
        />

        <div className="container mx-auto px-4 md:px-6 py-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="gotham-bold text-2xl md:text-3xl text-gray-900 mb-2">
              ¡Hola, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-gray-500">Aquí está el resumen de tu cuenta de Audio-Lector</p>
          </div>

          {/* PROMINENT: Acceso al Catálogo de Audiolibros */}
          {stats?.membership_status === 'active' && (
            <div 
              className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-2xl shadow-lg p-6 mb-4 relative overflow-hidden"
              data-testid="catalog-access-banner"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-white rounded-full"></div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white rounded-full"></div>
              </div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_connect-referral-1/artifacts/m1cexuiu_Viubux%20-%20Logo%20-%20White%20-%20Artboard%201%20copy%281%29.png"
                      alt="Viubux"
                      className="w-10 h-10 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div>
                    <h2 className="gotham-bold text-xl md:text-2xl text-white mb-1">
                      ¡Accede a tu Catálogo de Audiolibros!
                    </h2>
                    <p className="text-white/80 text-sm md:text-base">
                      Disfruta de cientos de títulos exclusivos con tu membresía activa
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="access-catalog-btn"
                  onClick={() => window.open('https://app.viubux.com/library/filter?collection=VIUBUXCONNECT&orderBy=sales', '_blank')}
                  className="bg-white text-violet-600 hover:bg-gray-100 px-8 py-6 rounded-xl gotham-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  <span className="mr-2">🎧</span>
                  Ir al Catálogo
                </Button>
              </div>
            </div>
          )}

          {/* NEW: Acceso a ViubuxPlay */}
          {(stats?.membership_status === 'active' || stats?.membership_status === 'free') && (
            <div 
              className="bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 rounded-2xl shadow-lg p-6 mb-8 relative overflow-hidden"
              data-testid="viubuxplay-access-banner"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white rounded-full"></div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white rounded-full"></div>
              </div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="gotham-bold text-xl md:text-2xl text-white mb-1">
                      ¡NUEVO! Acceso a ViubuxPlay
                    </h2>
                    <p className="text-white/80 text-sm md:text-base">
                      Videolibros, Booktrailers, Movies, Conferencias y Podcasts
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="access-viubuxplay-btn"
                  onClick={() => window.open('https://play.viubux.com', '_blank')}
                  className="bg-white text-pink-600 hover:bg-gray-100 px-8 py-6 rounded-xl gotham-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  <span className="mr-2">▶️</span>
                  Ir a ViubuxPlay
                </Button>
              </div>
            </div>
          )}

          {/* NEW: Acceso a Viubux Reader */}
          {(stats?.membership_status === 'active' || stats?.membership_status === 'free') && (
            <div 
              className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-2xl shadow-lg p-6 mb-8 relative overflow-hidden"
              data-testid="viubuxreader-access-banner"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white rounded-full"></div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white rounded-full"></div>
              </div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="gotham-bold text-xl md:text-2xl text-white mb-1">
                      Acceso a Viubux Reader
                    </h2>
                    <p className="text-white/80 text-sm md:text-base">
                      Audiolibros, eBooks, Podcasts y contenido exclusivo de la colección ViubuxConnect
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="access-viubuxreader-btn"
                  onClick={() => window.open('https://app.viubux.com', '_blank')}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 rounded-xl gotham-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  <span className="mr-2">📚</span>
                  Ir a Viubux Reader
                </Button>
              </div>
            </div>
          )}

          {/* Badges Section */}
          {badges.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-violet-500" />
                  <span className="verdana-regular text-gray-600 text-sm">Mis Insignias:</span>
                </div>
                <div className="flex gap-3 flex-wrap">
                  {badges.map((badge, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: `${badge.color}15`, border: `1px solid ${badge.color}30` }}
                      title={badge.description}
                    >
                      <span className="text-lg">{badge.icon}</span>
                      <span className="verdana-regular text-gray-700 text-xs">{badge.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Últimos contenidos publicados */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-slate-950 via-violet-950/40 to-slate-950 border border-violet-500/20 mb-8" data-testid="dashboard-latest-content">
            <LatestContentCarousel theme="violet" compact title="Últimos contenidos publicados" subtitle="Explora lo más reciente en Viubux" />
          </div>

          {/* Botones grandes de activación para AUTORES */}
          {user?.user_type === 'author' && authorOptions && (
            <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="author-activation-buttons">
              {/* Membresía */}
              <button
                onClick={() => openAuthorCheckout('membership')}
                className={`text-left rounded-2xl p-6 border-2 transition-all group ${
                  authorOptions.membership.active
                    ? 'border-green-500/40 bg-green-500/5'
                    : 'border-amber-500/50 bg-gradient-to-br from-amber-500/15 to-amber-600/5 hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/20'
                }`}
                data-testid="activate-membership-big-btn"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  <span className="gotham-bold text-white text-base">Membresía ViubuxConnect</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`gotham-bold text-2xl ${authorOptions.membership.active ? 'text-green-400' : 'text-amber-400'}`}>
                      {authorOptions.membership.active ? 'ACTIVA' : 'INACTIVA'}
                    </span>
                    <p className="verdana-regular text-white/60 text-sm mt-1">
                      {authorOptions.membership.active
                        ? 'Acceso al Pool de Autores activo'
                        : `Actívala ahora · ${authorOptions.currency_symbol}${authorOptions.membership.price.toLocaleString()} ${authorOptions.currency}/año`}
                    </p>
                  </div>
                  {!authorOptions.membership.active && (
                    <span className="bg-amber-500 text-black font-bold rounded-xl px-4 py-2 text-sm group-hover:bg-amber-400 whitespace-nowrap">Actívala ahora</span>
                  )}
                </div>
              </button>

              {/* Suscripción */}
              <button
                onClick={() => openAuthorCheckout('subscription')}
                className={`text-left rounded-2xl p-6 border-2 transition-all group ${
                  authorOptions.subscription.active
                    ? 'border-green-500/40 bg-green-500/5'
                    : 'border-purple-500/50 bg-gradient-to-br from-purple-500/15 to-pink-600/5 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-500/20'
                }`}
                data-testid="activate-subscription-big-btn"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <span className="gotham-bold text-white text-base">Suscripción ViubuxConnect</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`gotham-bold text-2xl ${authorOptions.subscription.active ? 'text-green-400' : 'text-purple-300'}`}>
                      {authorOptions.subscription.active ? 'ACTIVA' : 'INACTIVA'}
                    </span>
                    <p className="verdana-regular text-white/60 text-sm mt-1">
                      {authorOptions.subscription.active
                        ? 'Monetización del Pool activa'
                        : `Actívala ahora · elige mensual o anual`}
                    </p>
                  </div>
                  {!authorOptions.subscription.active && (
                    <span className="bg-purple-600 text-white font-bold rounded-xl px-4 py-2 text-sm group-hover:bg-purple-500 whitespace-nowrap">Actívala ahora</span>
                  )}
                </div>
              </button>
            </div>
          )}

          {/* Status Alert - Membership Inactive (solo consumidores) */}
          {stats?.membership_status !== 'active' && user?.user_type !== 'author' && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8" data-testid="inactive-membership-alert">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="gotham-bold text-gray-900 mb-1">Membresía Inactiva</h3>
                  <p className="verdana-regular text-gray-600 text-sm">
                    Activa tu membresía para acceder al catálogo de audiolibros y comenzar a ganar comisiones.
                  </p>
                </div>
                <Button
                  data-testid="activate-membership-btn"
                  onClick={() => handlePayment('consumer_subscription')}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-6"
                >
                  Activar Ahora
                </Button>
              </div>
            </div>
          )}

          {/* Subscription Alerts */}
          {subscriptionStatus?.show_subscription_button && stats?.membership_status === 'active' && (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 mb-8" data-testid="subscription-expiring-alert">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="gotham-bold text-gray-900 mb-1">
                    {subscriptionStatus.countdown_message || 'Tu suscripción está por vencer'}
                  </h3>
                  <p className="verdana-regular text-gray-600 text-sm">
                    {subscriptionStatus.days_remaining === 0 
                      ? 'Renueva hoy para mantener tu membresía activa.'
                      : `Te quedan ${subscriptionStatus.days_remaining} días. Renueva para seguir disfrutando.`
                    }
                  </p>
                </div>
                <Button
                  data-testid="renew-subscription-btn"
                  onClick={handleCreateSubscription}
                  disabled={subscriptionLoading}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {subscriptionLoading ? 'Procesando...' : 'Renovar'}
                </Button>
              </div>
            </div>
          )}

          {/* Stats Cards - Estilo app.viubux.com */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow" data-testid="stat-referrals">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-violet-600" />
                </div>
                <span className="gotham-medium text-gray-600">Referidos</span>
              </div>
              <div className="gotham-black text-4xl text-gray-900 mb-1">{stats?.total_referrals || 0}</div>
              <div className="verdana-regular text-sm text-gray-400">{stats?.active_referrals || 0} activos</div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow" data-testid="stat-earnings">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <span className="gotham-medium text-gray-600">Ganancias Totales</span>
              </div>
              <div className="gotham-black text-4xl text-blue-500 mb-1">{formatCurrency(stats?.total_earnings)}</div>
              <div className="verdana-regular text-sm text-gray-400">MXN</div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex justify-between text-xs">
                  <span className="text-green-600">Bono Rápido (25%):</span>
                  <span className="text-gray-700 font-medium">{formatCurrency(stats?.rapid_bonus_earnings)}</span>
                </div>
              </div>
            </div>

            {/* Tarjeta de Ventas Directas / Regalías - Solo para Autores (Tema Claro) */}
            {user?.user_type === 'author' && (
              <div className="bg-white rounded-2xl shadow-sm border border-yellow-200 p-6 hover:shadow-md transition-shadow" data-testid="stat-royalties-light">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span className="gotham-medium text-gray-600">Ventas Directas</span>
                </div>
                <div className="gotham-black text-4xl text-yellow-500 mb-1">
                  {formatCurrency(stats?.royalties_total || 0)}
                </div>
                <div className="verdana-regular text-sm text-gray-400">
                  {stats?.royalties_units_sold || 0} unidades vendidas
                </div>
                <div className="mt-3 pt-3 border-t border-yellow-100 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-green-500">Pagado:</span>
                    <span className="text-gray-700 font-medium">{formatCurrency(stats?.royalties_paid || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-yellow-500">Pendiente:</span>
                    <span className="text-gray-700 font-medium">{formatCurrency(stats?.royalties_pending || 0)}</span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/royalties')}
                  variant="outline"
                  className="w-full mt-4 border-yellow-300 text-yellow-600 hover:bg-yellow-50"
                  size="sm"
                >
                  Ver Reportes
                </Button>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow" data-testid="stat-available">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="gotham-medium text-gray-600">Disponible</span>
              </div>
              <div className={`gotham-black text-4xl mb-1 ${stats?.total_earnings > 0 ? 'text-emerald-500' : 'text-gray-400'}`}>
                {formatCurrency(stats?.total_earnings)}
              </div>
              <div className="verdana-regular text-sm text-gray-400">
                {stats?.total_earnings >= 500 ? 'Puedes solicitar retiro' : 'Mínimo $500 MXN'}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow" data-testid="stat-status">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stats?.membership_status === 'active' ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Check className={`w-5 h-5 ${stats?.membership_status === 'active' ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <span className="gotham-medium text-gray-600">Estado</span>
              </div>
              <div className={`gotham-bold text-2xl mb-1 ${stats?.membership_status === 'active' ? 'text-green-500' : 'text-red-500'}`}>
                {stats?.membership_status === 'active' ? 'Activo' : 'Inactivo'}
              </div>
              <div className="verdana-regular text-sm text-gray-400">
                {stats?.membership_status === 'active' ? 'Membresía vigente' : 'Requiere pago'}
              </div>
            </div>
          </div>

          {/* Tabs - Estilo app.viubux.com */}
          <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto pb-px">
            <button
              data-testid="tab-overview"
              onClick={() => setActiveTab('overview')}
              className={`gotham-medium px-4 py-3 transition-colors whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              Resumen
            </button>
            <button
              data-testid="tab-referrals"
              onClick={() => setActiveTab('referrals')}
              className={`gotham-medium px-4 py-3 transition-colors whitespace-nowrap ${
                activeTab === 'referrals'
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              Mis Referidos
            </button>
            <button
              data-testid="tab-commissions"
              onClick={() => setActiveTab('commissions')}
              className={`gotham-medium px-4 py-3 transition-colors whitespace-nowrap ${
                activeTab === 'commissions'
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              Comisiones
            </button>
            <button
              data-testid="tab-payments"
              onClick={() => setActiveTab('payments')}
              className={`gotham-medium px-4 py-3 transition-colors whitespace-nowrap ${
                activeTab === 'payments'
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              Pagos
            </button>
            <button
              data-testid="tab-simulator"
              onClick={() => setActiveTab('simulator')}
              className={`gotham-medium px-4 py-3 transition-colors whitespace-nowrap ${
                activeTab === 'simulator'
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              Simulador
            </button>
          </div>

          {/* Tab Content - Consumer Dashboard */}
          {activeTab === 'overview' && (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Referral Link Card - CON USERNAME */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="referral-link-card">
                <h3 className="gotham-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <Copy className="w-5 h-5 text-violet-500" />
                  Tus Enlaces de Referido
                </h3>
                
                {/* Enlace personalizado con username */}
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">
                    <span className="text-green-600 font-medium">✨ Tu enlace personalizado:</span>
                  </p>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="verdana-regular text-green-700 text-sm break-all font-mono">
                      {`https://connect.viubux.com?ref=${user?.username || user?.referral_code}`}
                    </p>
                  </div>
                  <Button
                    data-testid="copy-referral-link-username"
                    onClick={() => {
                      navigator.clipboard.writeText(`https://connect.viubux.com?ref=${user?.username || user?.referral_code}`);
                      toast.success('¡Enlace personalizado copiado!');
                    }}
                    className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Enlace Personalizado
                  </Button>
                </div>

                {/* Enlace con código (alternativo) */}
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-2">Enlace alternativo (con código):</p>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="verdana-regular text-gray-500 text-xs break-all font-mono">
                      {`https://connect.viubux.com?ref=${user?.referral_code}`}
                    </p>
                  </div>
                  <Button
                    data-testid="copy-referral-link-code"
                    onClick={() => {
                      navigator.clipboard.writeText(`https://connect.viubux.com?ref=${user?.referral_code}`);
                      toast.success('¡Enlace copiado!');
                    }}
                    variant="outline"
                    className="w-full mt-2 border-gray-200 text-gray-600 rounded-xl text-sm"
                    size="sm"
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    Copiar
                  </Button>
                </div>
              </div>

              {/* QR Code Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="qr-code-card">
                <h3 className="gotham-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-xl">📱</span>
                  Código QR
                </h3>
                <div className="flex items-center gap-6">
                  <div className="bg-white p-4 rounded-xl border border-gray-200">
                    <QRCodeSVG 
                      value={`https://connect.viubux.com?ref=${user?.username || user?.referral_code}`}
                      size={120}
                      level="H"
                      includeMargin={true}
                      imageSettings={{
                        src: "/viubux_logo_web.png",
                        x: undefined,
                        y: undefined,
                        height: 24,
                        width: 24,
                        excavate: true,
                      }}
                    />
                  </div>
                  <div>
                    <p className="verdana-regular text-gray-600 text-sm mb-3">
                      Comparte este código QR para que otros se registren con tu enlace de referido.
                    </p>
                    <p className="text-violet-600 font-medium text-sm">
                      Usuario: @{user?.username || user?.referral_code}
                    </p>
                  </div>
                </div>
              </div>

              {/* Subscription Info Card */}
              {stats?.membership_status === 'active' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="subscription-info-card">
                  <h3 className="gotham-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-500" />
                    Información de Suscripción
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-500">Estado</span>
                      <span className="text-green-600 font-medium flex items-center gap-1">
                        <Check className="w-4 h-4" /> Activo
                      </span>
                    </div>
                    {subscriptionStatus?.expires_at && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-500">Válido hasta</span>
                        <span className="text-gray-700 font-medium">
                          {new Date(subscriptionStatus.expires_at).toLocaleDateString('es-MX', { 
                            year: 'numeric', month: 'long', day: 'numeric' 
                          })}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-500">Plan</span>
                      <span className="text-blue-500 font-bold">$199 MXN/mes</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'referrals' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="gotham-bold text-lg text-gray-900 mb-6">Mis Referidos</h3>
              {referrals.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">Aún no tienes referidos</p>
                  <p className="text-gray-300 text-sm">Comparte tu enlace para comenzar a ganar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Nombre</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Email</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Tipo</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Estado</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((referral, index) => (
                        <tr key={index} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-4 text-gray-900">{referral.name}</td>
                          <td className="py-4 px-4 text-gray-500 text-sm">{referral.email}</td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              referral.user_type === 'author' 
                                ? 'bg-violet-100 text-violet-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {referral.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              referral.membership_status === 'active' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {referral.membership_status === 'active' ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-gray-400 text-sm">
                            {new Date(referral.created_at).toLocaleDateString('es-MX')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'commissions' && (
            <div className="space-y-6">
              {/* Referral Commissions */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="gotham-bold text-lg text-gray-900 mb-6 flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-500" />
                  Comisiones por Referidos
                </h3>
                {commissions.length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 mb-1">Aún no tienes comisiones por referidos</p>
                    <p className="text-gray-300 text-sm">Las comisiones aparecerán cuando tus referidos se activen</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Tipo</th>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">De</th>
                          <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">Monto</th>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Estado</th>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissions.map((commission, index) => (
                          <tr key={index} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="py-4 px-4">
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Bono Rápido (25%)
                              </span>
                            </td>
                            <td className="py-4 px-4 text-gray-500 text-sm">{commission.from_user_name || 'Usuario'}</td>
                            <td className="py-4 px-4 text-right text-blue-500 font-bold">{formatCurrency(commission.amount)}</td>
                            <td className="py-4 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                commission.payout_status === 'completed' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {commission.payout_status === 'completed' ? 'Pagado' : 'Pendiente'}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-gray-400 text-sm">
                              {new Date(commission.created_at).toLocaleDateString('es-MX')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Production Commissions (from cotizaciones.viubux.com) - Solo para Autores */}
              {user?.user_type === 'author' && (
              <div className="bg-white rounded-2xl shadow-sm border border-purple-200 p-6">
                <h3 className="gotham-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-purple-500" />
                  Comisiones de Producción
                </h3>
                <p className="text-gray-500 text-sm mb-6">
                  5% de comisión por trabajos referidos desde cotizaciones.viubux.com
                </p>
                
                {productionCommissions?.count > 0 ? (
                  <>
                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-purple-50 rounded-lg p-4 text-center">
                        <div className="text-gray-500 text-xs mb-1">Total Ganado</div>
                        <div className="gotham-bold text-xl text-purple-600">
                          {formatCurrency(productionCommissions.total_earned)}
                        </div>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4 text-center">
                        <div className="text-gray-500 text-xs mb-1">Pendiente</div>
                        <div className="gotham-bold text-xl text-yellow-600">
                          {formatCurrency(productionCommissions.pending)}
                        </div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <div className="text-gray-500 text-xs mb-1">Pagado</div>
                        <div className="gotham-bold text-xl text-green-600">
                          {formatCurrency(productionCommissions.paid)}
                        </div>
                      </div>
                    </div>

                    {/* Commission List */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Trabajo</th>
                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Cliente</th>
                            <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">Monto Trabajo</th>
                            <th className="text-right py-3 px-4 text-gray-500 font-medium text-sm">Comisión (5%)</th>
                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Estado</th>
                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Fecha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productionCommissions.commissions?.map((pc, index) => (
                            <tr key={pc.id || index} className="border-b border-gray-50 hover:bg-purple-50/30 transition-colors">
                              <td className="py-4 px-4">
                                <div className="text-gray-800 font-medium text-sm max-w-xs truncate">
                                  {pc.job_description}
                                </div>
                                <div className="text-gray-400 text-xs">{pc.job_id}</div>
                              </td>
                              <td className="py-4 px-4">
                                <div className="text-gray-600 text-sm">{pc.client_name || '-'}</div>
                                <div className="text-gray-400 text-xs">{pc.client_email}</div>
                              </td>
                              <td className="py-4 px-4 text-right text-gray-600">
                                {formatCurrency(pc.transaction_amount)}
                              </td>
                              <td className="py-4 px-4 text-right text-purple-600 font-bold">
                                {formatCurrency(pc.commission_amount)}
                              </td>
                              <td className="py-4 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  pc.status === 'paid' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {pc.status === 'paid' ? 'Pagado' : 'Pendiente'}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-gray-400 text-sm">
                                {new Date(pc.created_at).toLocaleDateString('es-MX')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 mb-1">Sin comisiones de producción</p>
                    <p className="text-gray-300 text-sm">Comparte tu código de referido en cotizaciones.viubux.com</p>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="space-y-6">
              {/* Canjear Comisiones por Suscripción */}
              <RedeemCommissions token={token} onSuccess={() => window.location.reload()} />
              
              {/* Configuración de Pagos */}
              <PaymentSettings token={token} userData={user} theme="light" />

              {/* Invitación Nubank (NU) */}
              <NubankInvite theme="light" />

              {/* Seguridad de la Cuenta */}
              <AccountSecurity />
            </div>
          )}

          {activeTab === 'simulator' && (
            <EarningsSimulator userType="consumer" theme="light" user={user} />
          )}
        </div>

        <PaymentPlanModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          userType={user?.user_type}
          token={token}
        />
      </div>
    );
  }

  // Dashboard original para Autores (mantiene estilo oscuro)
  return (
    <div className="min-h-screen dark overflow-x-hidden" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
      {/* Header */}
      <header className="border-b border-purple-500/20 backdrop-blur-sm" style={{ background: 'rgba(39, 43, 150, 0.3)' }}>
        <div className="container mx-auto px-4 sm:px-6 py-3">
          {/* Top row - Logo and user actions */}
          <div className="flex justify-between items-center mb-2 sm:mb-0">
            <div className="flex items-center">
              <img 
                src="/viubux_logo_web.png" 
                alt="Viubux Connect" 
                className="h-8 sm:h-10 md:h-12 w-auto object-contain brightness-0 invert"
                style={{ maxWidth: '150px' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="gotham-bold text-xl sm:text-2xl text-white">ViubuxConnect</span>';
                }}
              />
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className="verdana-regular text-white/80 hidden lg:block text-sm">{user?.email}</span>
              {/* Mobile menu button */}
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="border-purple-500/50 text-white hover:bg-purple-500/20 px-2 sm:px-4"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Salir</span>
              </Button>
              {/* Language Selector */}
              <LanguageToggle />
            </div>
          </div>
          {/* Bottom row - Navigation buttons (scrollable on mobile) */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 scrollbar-hide">
            {!isAuthorLite && (
              <Button
                data-testid="networking-btn"
                onClick={() => navigate('/networking')}
                variant="outline"
                className="border-green-500/50 text-green-400 hover:bg-green-500/20 whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
              >
                <Users className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Networking</span>
              </Button>
            )}
            {user?.user_type === 'author' && !isAuthorLite && (
              <Button
                data-testid="royalties-btn"
                onClick={() => navigate('/royalties')}
                variant="outline"
                className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20 whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
              >
                <Building2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Regalías</span>
              </Button>
            )}
            <Button
              data-testid="messages-btn"
              onClick={() => setShowMessages(true)}
              variant="outline"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20 relative whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
            >
              <MessageCircle className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Mensajes</span>
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadMessages}
                </span>
              )}
            </Button>
            {user?.user_type === 'author' && (
              <>
                <Button
                  data-testid="publicala-btn"
                  onClick={() => setShowPublicalaUploader(true)}
                  variant="outline"
                  className="border-pink-500/50 text-pink-400 hover:bg-pink-500/20 whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
                >
                  <Upload className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Publica en Reader.Viubux.com</span>
                </Button>
                <Button
                  data-testid="viubuxplay-upload-btn"
                  onClick={() => setShowViubuxPlayUploader(true)}
                  variant="outline"
                  className="border-rose-500/50 text-rose-400 hover:bg-rose-500/20 whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
                >
                  <Upload className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Publica en Play.Viubux</span>
                </Button>
              </>
            )}
            <Button
              data-testid="profile-btn"
              onClick={() => navigate('/profile')}
              variant="outline"
              className="border-purple-500/50 text-white hover:bg-purple-500/20 whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
            >
              <User className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Mi Perfil</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Messages Panel */}
      <MessagesPanel 
        token={token} 
        isOpen={showMessages} 
        onClose={() => {
          setShowMessages(false);
          fetchUnreadMessages();
        }} 
      />

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Últimos contenidos publicados */}
        <div className="card-viubux-dark rounded-2xl overflow-hidden border-purple-500/30 mb-4 sm:mb-6" data-testid="dashboard-latest-content">
          <LatestContentCarousel theme="violet" compact title="Últimos contenidos publicados" subtitle="Explora lo más reciente en Viubux" />
        </div>

        {/* Botones grandes de activación para AUTORES */}
        {user?.user_type === 'author' && authorOptions && (
          <div className="grid md:grid-cols-2 gap-4 mb-4 sm:mb-6" data-testid="author-activation-buttons">
            {/* Membresía */}
            <button
              onClick={() => openAuthorCheckout('membership')}
              className={`text-left rounded-2xl p-5 sm:p-6 border-2 transition-all group ${
                authorOptions.membership.active
                  ? 'border-green-500/40 bg-green-500/5'
                  : 'border-amber-500/50 bg-gradient-to-br from-amber-500/15 to-amber-600/5 hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/20'
              }`}
              data-testid="activate-membership-big-btn"
            >
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="gotham-bold text-white text-base">Membresía ViubuxConnect</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className={`gotham-bold text-2xl ${authorOptions.membership.active ? 'text-green-400' : 'text-amber-400'}`}>
                    {authorOptions.membership.active ? 'ACTIVA' : 'INACTIVA'}
                  </span>
                  <p className="verdana-regular text-white/60 text-sm mt-1">
                    {authorOptions.membership.active
                      ? 'Acceso al Pool de Autores activo'
                      : `Actívala ahora · ${authorOptions.currency_symbol}${authorOptions.membership.price.toLocaleString()} ${authorOptions.currency}/año`}
                  </p>
                </div>
                {!authorOptions.membership.active && (
                  <span className="bg-amber-500 text-black font-bold rounded-xl px-4 py-2 text-sm group-hover:bg-amber-400 whitespace-nowrap">Actívala ahora</span>
                )}
              </div>
            </button>

            {/* Suscripción */}
            <button
              onClick={() => openAuthorCheckout('subscription')}
              className={`text-left rounded-2xl p-5 sm:p-6 border-2 transition-all group ${
                authorOptions.subscription.active
                  ? 'border-green-500/40 bg-green-500/5'
                  : 'border-purple-500/50 bg-gradient-to-br from-purple-500/15 to-pink-600/5 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-500/20'
              }`}
              data-testid="activate-subscription-big-btn"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                <span className="gotham-bold text-white text-base">Suscripción ViubuxConnect</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className={`gotham-bold text-2xl ${authorOptions.subscription.active ? 'text-green-400' : 'text-purple-300'}`}>
                    {authorOptions.subscription.active ? 'ACTIVA' : 'INACTIVA'}
                  </span>
                  <p className="verdana-regular text-white/60 text-sm mt-1">
                    {authorOptions.subscription.active
                      ? 'Monetización del Pool activa'
                      : 'Actívala ahora · elige mensual o anual'}
                  </p>
                </div>
                {!authorOptions.subscription.active && (
                  <span className="bg-purple-600 text-white font-bold rounded-xl px-4 py-2 text-sm group-hover:bg-purple-500 whitespace-nowrap">Actívala ahora</span>
                )}
              </div>
            </button>
          </div>
        )}
        {/* Badges Section */}
        {badges.length > 0 && (
          <div className="card-viubux-dark mb-4 sm:mb-6 border-purple-500/30 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                <span className="verdana-regular text-white/80 text-xs sm:text-sm">Mis Insignias:</span>
              </div>
              <div className="flex gap-2 sm:gap-3 flex-wrap">
                {badges.map((badge, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full"
                    style={{ backgroundColor: `${badge.color}20`, border: `1px solid ${badge.color}50` }}
                    title={badge.description}
                  >
                    <span className="text-base sm:text-lg">{badge.icon}</span>
                    <span className="verdana-regular text-white text-xs">{badge.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Author Verification Required - Show before payment for unverified authors */}
        {user?.user_type === 'author' && 
         stats?.membership_status !== 'active' && 
         authorVerificationStatus && 
         !authorVerificationStatus.author_verified && (
          <div className="mb-8">
            <AuthorVerification 
              token={token} 
              onVerificationComplete={() => fetchAuthorVerificationStatus()}
            />
          </div>
        )}

        {/* Status Alert */}
        {stats?.membership_status !== 'active' && (
          <div className="card-viubux-dark mb-8 border-yellow-500/50" data-testid="inactive-membership-alert">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-yellow-500" />
              </div>
              <div className="flex-1">
                <h3 className="gotham-bold text-white mb-1">Membresía Inactiva</h3>
                <p className="verdana-regular text-white/70 text-sm">
                  {user?.user_type === 'author' && authorVerificationStatus && !authorVerificationStatus.author_verified
                    ? 'Completa tu verificación de autor antes de activar tu membresía.'
                    : 'Activa tu membresía para obtener tu código de acceso y comenzar a ganar comisiones.'
                  }
                </p>
              </div>
              {/* Only show payment button if author is verified or user is consumer */}
              {(user?.user_type !== 'author' || (authorVerificationStatus && authorVerificationStatus.author_verified)) && (
                <Button
                  data-testid="activate-membership-btn"
                  onClick={() => handlePayment(user?.user_type === 'author' ? 'author_enrollment' : 'consumer_subscription')}
                  className="btn-viubux"
                >
                  Activar Ahora
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Subscription Alert for Authors - Shows 7 days before expiration */}
        {subscriptionStatus?.show_subscription_button && stats?.membership_status === 'active' && (
          <div className="card-viubux-dark mb-8 border-orange-500/50" data-testid="subscription-expiring-alert">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-orange-500" />
              </div>
              <div className="flex-1">
                <h3 className="gotham-bold text-white mb-1">
                  {subscriptionStatus.countdown_message || 'Tu suscripción está por vencer'}
                </h3>
                <p className="verdana-regular text-white/70 text-sm">
                  {subscriptionStatus.days_remaining === 0 
                    ? 'Renueva hoy para mantener tu membresía activa y seguir recibiendo comisiones.'
                    : `Te quedan ${subscriptionStatus.days_remaining} días. Renueva tu suscripción ($199 MXN/mes) para mantener tu membresía activa.`
                  }
                </p>
              </div>
              <Button
                data-testid="renew-subscription-btn"
                onClick={handleCreateSubscription}
                disabled={subscriptionLoading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {subscriptionLoading ? 'Procesando...' : 'Renovar Suscripción'}
              </Button>
            </div>
          </div>
        )}

        {/* Grace Period Warning */}
        {subscriptionStatus?.is_in_grace_period && (
          <div className="card-viubux-dark mb-8 border-red-500/50" data-testid="grace-period-alert">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-500 animate-pulse" />
              </div>
              <div className="flex-1">
                <h3 className="gotham-bold text-white mb-1">
                  ¡Período de Gracia! {subscriptionStatus.grace_days_remaining} días restantes
                </h3>
                <p className="verdana-regular text-white/70 text-sm">
                  Tu suscripción ha expirado. Tienes {subscriptionStatus.grace_days_remaining} días para renovar antes de que tu cuenta sea pausada y pierdas acceso a cobrar comisiones.
                </p>
              </div>
              <Button
                data-testid="renew-urgent-btn"
                onClick={handleCreateSubscription}
                disabled={subscriptionLoading}
                className="bg-red-500 hover:bg-red-600 text-white animate-pulse"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {subscriptionLoading ? 'Procesando...' : '¡Renovar Ahora!'}
              </Button>
            </div>
          </div>
        )}

        {/* Subscription Paused Alert */}
        {subscriptionStatus?.subscription_paused && (
          <div className="card-viubux-dark mb-8 border-gray-500/50" data-testid="subscription-paused-alert">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-500/20 flex items-center justify-center flex-shrink-0">
                <PauseCircle className="w-6 h-6 text-gray-400" />
              </div>
              <div className="flex-1">
                <h3 className="gotham-bold text-white mb-1">Membresía Pausada</h3>
                <p className="verdana-regular text-white/70 text-sm">
                  Tu membresía ha sido pausada por falta de pago. No puedes recibir comisiones ni realizar cobros hasta que renueves tu suscripción.
                </p>
              </div>
              <Button
                data-testid="reactivate-btn"
                onClick={() => setShowPaymentModal(true)}
                disabled={subscriptionLoading}
                className="btn-viubux"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {subscriptionLoading ? 'Procesando...' : 'Reactivar Membresía'}
              </Button>
            </div>
          </div>
        )}

        {/* Acceso a Plataformas Viubux - Para TODOS los usuarios activos o free */}
        {(stats?.membership_status === 'active' || stats?.membership_status === 'free') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Viubux Play */}
            <div 
              className="bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 rounded-2xl shadow-lg p-5 relative overflow-hidden"
              data-testid="viubuxplay-banner-main"
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-white rounded-full"></div>
              </div>
              <div className="relative z-10 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <img src="/vbx-play-white.png" alt="Viubux Play" className="h-16 w-auto object-contain mb-2" onError={(e)=>{e.target.style.display='none';}} />
                  <p className="text-white/85 text-xs leading-snug">Videolibros, Cursos, Podcasts, Audiolibros, Videos, Movies, Conferencias, Masterclases, MasterTalks, Entrevistas, Música, Meditaciones y más.</p>
                </div>
                <Button
                  data-testid="go-viubuxplay-btn"
                  onClick={() => window.open('https://play.viubux.com', '_blank')}
                  className="bg-white text-pink-600 hover:bg-gray-100 px-4 py-2 rounded-lg gotham-bold text-sm flex-shrink-0"
                >
                  ▶️ Ir
                </Button>
              </div>
            </div>
            
            {/* Viubux Reader */}
            <div 
              className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-2xl shadow-lg p-5 relative overflow-hidden"
              data-testid="viubuxreader-banner-main"
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-white rounded-full"></div>
              </div>
              <div className="relative z-10 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <img src="/vbx-reader-white.png" alt="Viubux Reader" className="h-16 w-auto object-contain mb-2" onError={(e)=>{e.target.style.display='none';}} />
                  <p className="text-white/85 text-xs leading-snug">Ebooks, EPubs, Audiolibros, AudioCursos, Música, Podcasts en audio, PDFs y más.</p>
                </div>
                <Button
                  data-testid="go-viubuxreader-btn"
                  onClick={() => window.open('https://app.viubux.com', '_blank')}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-4 py-2 rounded-lg gotham-bold text-sm flex-shrink-0"
                >
                  📚 Ir
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <Card className="card-viubux-dark border-purple-500/20" data-testid="stat-referrals">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="gotham-medium text-white flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#7012b2' }} />
                Referidos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="gotham-black text-2xl sm:text-4xl text-white mb-1">{stats?.total_referrals || 0}</div>
              <div className="verdana-regular text-xs sm:text-sm text-white/60">{stats?.active_referrals || 0} activos</div>
            </CardContent>
          </Card>

          <Card className="card-viubux-dark border-purple-500/20" data-testid="stat-earnings">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="gotham-medium text-white flex items-center gap-2 text-sm sm:text-base">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#7012b2' }} />
                Ganancias Totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="gotham-black text-2xl sm:text-4xl text-white mb-1">{formatCurrency(stats?.total_earnings)}</div>
              <div className="verdana-regular text-xs sm:text-sm text-white/60">MXN</div>
              {/* Desglose de ganancias */}
              <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-purple-500/20 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-green-400">Bono Rápido (25%):</span>
                  <span className="text-white">{formatCurrency(stats?.rapid_bonus_earnings)}</span>
                </div>
                {user?.user_type === 'author' && (
                  <div className="flex justify-between text-xs">
                    <span className="text-purple-400">Pool de Autores:</span>
                    <span className="text-white">{formatCurrency(stats?.pool_earnings)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tarjeta de Ventas Directas / Regalías - Solo para Autores */}
          {user?.user_type === 'author' && (
            <Card className="card-viubux-dark border-yellow-500/20" data-testid="stat-royalties">
              <CardHeader className="pb-2 sm:pb-3">
                <CardTitle className="gotham-medium text-white flex items-center gap-2 text-sm sm:text-base">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                  Ventas Directas (Regalías)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="gotham-black text-2xl sm:text-4xl text-yellow-400 mb-1">
                  {formatCurrency(stats?.royalties_total || 0)}
                </div>
                <div className="verdana-regular text-xs sm:text-sm text-white/60">
                  {stats?.royalties_units_sold || 0} unidades vendidas
                </div>
                {/* Desglose de regalías */}
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-yellow-500/20 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-green-400">Pagado:</span>
                    <span className="text-white">{formatCurrency(stats?.royalties_paid || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-yellow-400">Pendiente:</span>
                    <span className="text-white">{formatCurrency(stats?.royalties_pending || 0)}</span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/royalties')}
                  variant="outline"
                  className="w-full mt-4 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                  size="sm"
                >
                  Ver Reportes de Regalías
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="card-viubux-dark border-purple-500/20" data-testid="stat-available">
            <CardHeader className="pb-3">
              <CardTitle className="gotham-medium text-white flex items-center gap-2">
                <Wallet className="w-5 h-5" style={{ color: '#7012b2' }} />
                Disponible para Cobrar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="gotham-black text-4xl text-white mb-1" style={{ color: stats?.total_earnings > 0 ? '#10b981' : 'white' }}>
                {formatCurrency(stats?.total_earnings)}
              </div>
              <div className="verdana-regular text-sm text-white/60">
                {stats?.total_earnings >= 500 ? 'Puedes solicitar retiro' : 'Mínimo $500 MXN para retirar'}
              </div>
            </CardContent>
          </Card>

          <Card className="card-viubux-dark border-purple-500/20" data-testid="stat-status">
            <CardHeader className="pb-3">
              <CardTitle className="gotham-medium text-white flex items-center gap-2">
                <Check className="w-5 h-5" style={{ color: '#7012b2' }} />
                Estado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="gotham-bold text-2xl mb-1" style={{ 
                color: subscriptionStatus?.subscription_paused 
                  ? '#f59e0b' 
                  : stats?.membership_status === 'active' 
                    ? '#10b981' 
                    : '#ef4444' 
              }}>
                {subscriptionStatus?.subscription_paused 
                  ? 'Pausado' 
                  : stats?.membership_status === 'active' 
                    ? 'Activo' 
                    : 'Inactivo'}
              </div>
              <div className="verdana-regular text-sm text-white/60">
                {subscriptionStatus?.subscription_paused 
                  ? 'Requiere reactivación' 
                  : subscriptionStatus?.grace_period 
                    ? 'En período de gracia' 
                    : stats?.membership_status === 'active' 
                      ? 'Membresía vigente' 
                      : 'Requiere pago'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pool Share Card - Solo para Autores */}
        {user?.user_type === 'author' && poolShare && (
          <div className="mb-8">
            <Card className="card-viubux-dark border-purple-500/30 bg-gradient-to-r from-purple-900/20 to-purple-800/10" data-testid="pool-share-summary">
              <CardContent className="py-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <TrendingUp className="w-8 h-8 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="gotham-bold text-white text-lg">Tu Parte del Pool de Autores</h3>
                      <p className="verdana-regular text-white/60 text-sm">
                        Plan: <span className="text-purple-300 gotham-medium">{poolShare.plan}</span> • {poolShare.direct_referrals_count} referidos activos
                      </p>
                    </div>
                  </div>
                  <div className="text-center md:text-right">
                    <div className="gotham-black text-4xl text-green-400">
                      {formatCurrency(poolShare.pool_share)}
                    </div>
                    <div className="verdana-regular text-white/60 text-sm">MXN este mes</div>
                  </div>
                </div>
                
                {/* Referral Multiplier Table */}
                <div className="mt-4 pt-4 border-t border-purple-500/20">
                  <p className="verdana-regular text-white/70 text-xs mb-3 gotham-medium">
                    📊 Multiplicador de Referidos - Tu % del Pool:
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
                    <div className={`rounded-lg p-2 ${poolShare.direct_referrals_count === 0 && user?.membership_status !== 'active' ? 'bg-purple-500/30 ring-2 ring-purple-400' : 'bg-white/5'}`}>
                      <div className="text-white/50">0 ref (sin susc.)</div>
                      <div className="text-purple-300 gotham-bold">5%</div>
                    </div>
                    <div className={`rounded-lg p-2 ${poolShare.direct_referrals_count === 0 && user?.membership_status === 'active' ? 'bg-purple-500/30 ring-2 ring-purple-400' : 'bg-white/5'}`}>
                      <div className="text-white/50">0 ref (activo)</div>
                      <div className="text-purple-300 gotham-bold">10%</div>
                    </div>
                    <div className={`rounded-lg p-2 ${poolShare.direct_referrals_count === 1 ? 'bg-purple-500/30 ring-2 ring-purple-400' : 'bg-white/5'}`}>
                      <div className="text-white/50">1 referido</div>
                      <div className="text-purple-300 gotham-bold">25%</div>
                    </div>
                    <div className={`rounded-lg p-2 ${poolShare.direct_referrals_count === 2 ? 'bg-purple-500/30 ring-2 ring-purple-400' : 'bg-white/5'}`}>
                      <div className="text-white/50">2 referidos</div>
                      <div className="text-purple-300 gotham-bold">50%</div>
                    </div>
                    <div className={`rounded-lg p-2 ${poolShare.direct_referrals_count === 3 ? 'bg-purple-500/30 ring-2 ring-purple-400' : 'bg-white/5'}`}>
                      <div className="text-white/50">3 referidos</div>
                      <div className="text-purple-300 gotham-bold">75%</div>
                    </div>
                    <div className={`rounded-lg p-2 ${poolShare.direct_referrals_count >= 4 ? 'bg-green-500/30 ring-2 ring-green-400' : 'bg-white/5'}`}>
                      <div className="text-white/50">4+ referidos</div>
                      <div className="text-green-300 gotham-bold">100%</div>
                    </div>
                  </div>
                  <p className="verdana-regular text-white/40 text-xs mt-2">
                    💡 Mantén tu suscripción activa y refiere a más autores para maximizar tu parte del pool.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Assistant/editor context banner */}
        {user?.is_acting_as_owner && (
          <div className="mb-6 rounded-xl bg-gradient-to-r from-violet-600/20 to-purple-600/10 border border-violet-500/30 px-4 py-3 flex items-center gap-3" data-testid="assistant-context-banner">
            <Briefcase className="w-5 h-5 text-violet-300 flex-shrink-0" />
            <p className="verdana-regular text-white/80 text-sm">
              Estás administrando la cuenta de <strong className="text-white">{user?.managing_owner_name}</strong> como <strong>asistente/editor</strong>. No tienes acceso a información sensible (usuario, correo, datos bancarios ni pagos).
            </p>
          </div>
        )}

        {/* Tabs - Scrollable on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
          <div className="flex gap-2 sm:gap-4 mb-6 border-b border-purple-500/20 min-w-max pb-px">
          {user?.user_type === 'author' && (
            <button
              data-testid="tab-benefits"
              onClick={() => setActiveTab('benefits')}
              className={`gotham-medium px-3 sm:px-4 py-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'benefits'
                  ? 'text-white border-b-2 border-green-500'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              🎁 Beneficios
            </button>
          )}
          <button
            data-testid="tab-overview"
            onClick={() => setActiveTab('overview')}
            className={`gotham-medium px-3 sm:px-4 py-2 transition-colors relative whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'overview'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            📢 Anuncios
            {announcementCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                {announcementCount > 99 ? '99+' : announcementCount}
              </span>
            )}
          </button>
          {/* Genealogía - Solo para Premium */}
          {!isAuthorLite && (
            <button
              data-testid="tab-referrals"
              onClick={() => setActiveTab('referrals')}
              className={`gotham-medium px-3 sm:px-4 py-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'referrals'
                  ? 'text-white border-b-2 border-purple-500'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Genealogía
            </button>
          )}
          {/* Comisiones - Solo para Premium */}
          {!isAuthorLite && (
            <button
              data-testid="tab-commissions"
              onClick={() => setActiveTab('commissions')}
              className={`gotham-medium px-3 sm:px-4 py-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'commissions'
                  ? 'text-white border-b-2 border-purple-500'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Comisiones
            </button>
          )}
          <button
            data-testid="tab-subscription"
            onClick={() => setActiveTab('subscription')}
            className={`gotham-medium px-3 sm:px-4 py-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'subscription'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Suscripción
          </button>
          <button
            data-testid="tab-settings"
            onClick={() => setActiveTab('settings')}
            className={`gotham-medium px-3 sm:px-4 py-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'settings'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Configuración
          </button>
          {/* Simulador - Solo para Author Premium */}
          {user?.user_type === 'author' && !isAuthorLite && (
            <button
              data-testid="tab-simulator"
              onClick={() => setActiveTab('simulator')}
              className={`gotham-medium px-3 sm:px-4 py-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'simulator'
                  ? 'text-white border-b-2 border-yellow-500'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Simulador
            </button>
          )}
          <button
            data-testid="tab-marketing-author"
            onClick={() => setActiveTab('marketing')}
            className={`gotham-medium px-3 sm:px-4 py-2 transition-colors flex items-center gap-1 whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'marketing'
                ? 'text-white border-b-2 border-violet-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Megaphone className="w-4 h-4" />
            Pro-Marketing
          </button>
          <button
            data-testid="tab-gallery-author"
            onClick={() => setActiveTab('gallery')}
            className={`gotham-medium px-3 sm:px-4 py-2 transition-colors flex items-center gap-1 whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'gallery'
                ? 'text-white border-b-2 border-violet-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            Mis Imágenes
          </button>
          </div>
        </div>

        {/* Banner Author Lite - Indicador Visual Mejorado */}
        {isAuthorLite && (
          <div className="mb-6 bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900 border border-purple-500/30 rounded-2xl p-6 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-purple-500 to-violet-600 p-3 rounded-xl shadow-lg shadow-purple-500/20">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-xl text-white mb-1">Perfil de Autor Lite</h3>
                    <p className="verdana-regular text-purple-300 text-sm">
                      Desbloquea todo el potencial de tu membresía
                    </p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-purple-500/20 px-3 py-1.5 rounded-full">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-300 text-sm font-medium">Plan Lite</span>
                </div>
              </div>

              {/* Blocked Features Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-black/30 border border-red-500/20 rounded-xl p-3 flex items-center gap-3">
                  <div className="bg-red-500/20 p-2 rounded-lg">
                    <Users className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Networking</p>
                    <p className="text-red-400 text-xs flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Bloqueado
                    </p>
                  </div>
                </div>
                
                <div className="bg-black/30 border border-red-500/20 rounded-xl p-3 flex items-center gap-3">
                  <div className="bg-red-500/20 p-2 rounded-lg">
                    <GitBranch className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Genealogía</p>
                    <p className="text-red-400 text-xs flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Bloqueado
                    </p>
                  </div>
                </div>
                
                <div className="bg-black/30 border border-red-500/20 rounded-xl p-3 flex items-center gap-3">
                  <div className="bg-red-500/20 p-2 rounded-lg">
                    <DollarSign className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Comisiones</p>
                    <p className="text-red-400 text-xs flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Bloqueado
                    </p>
                  </div>
                </div>
                
                <div className="bg-black/30 border border-red-500/20 rounded-xl p-3 flex items-center gap-3">
                  <div className="bg-red-500/20 p-2 rounded-lg">
                    <Calculator className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Simulador</p>
                    <p className="text-red-400 text-xs flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Bloqueado
                    </p>
                  </div>
                </div>
              </div>

              {/* Available Features */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                <p className="text-green-400 text-sm font-medium mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Funciones disponibles en tu plan:
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs">Reportes de Regalías</span>
                  <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs">Pro-Marketing</span>
                  <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs">Mensajes</span>
                  <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs">Perfil Público</span>
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button
                  onClick={() => setActiveTab('subscription')}
                  className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-purple-500/40 hover:scale-105"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  Actualizar a Premium
                </Button>
                <p className="text-white/50 text-sm">
                  Contacta a tu sponsor o administrador para más información
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Sección de Anuncios y Noticias */}
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader>
                <CardTitle className="gotham-bold text-xl text-white flex items-center gap-3">
                  📢 Anuncios y Noticias
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Mantente al día con las últimas novedades de ViubuxConnect
                </CardDescription>
              </CardHeader>
            </Card>
            
            {/* Blog/Noticias Section */}
            <BlogSection token={token} userType={user?.user_type} onPostRead={fetchAnnouncementCount} />
          </div>
        )}

        {/* Genealogía/Referrals - Solo para Premium */}
        {activeTab === 'referrals' && !isAuthorLite && (
          <Card className="card-viubux-dark border-purple-500/20" data-testid="referrals-table">
            <CardHeader>
              <CardTitle className="gotham-bold text-white">Tu Red de Referidos</CardTitle>
              <CardDescription className="verdana-regular text-white/70">
                Personas que se han registrado con tu código
              </CardDescription>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <div className="text-center py-12 text-white/60 verdana-regular">
                  Aún no tienes referidos. ¡Comparte tu link para comenzar!
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full verdana-regular">
                    <thead>
                      <tr className="border-b border-purple-500/20">
                        <th className="text-left py-3 px-4 text-white/80">Nombre</th>
                        <th className="text-left py-3 px-4 text-white/80">Email</th>
                        <th className="text-left py-3 px-4 text-white/80">Tipo</th>
                        <th className="text-left py-3 px-4 text-white/80">Estado</th>
                        <th className="text-left py-3 px-4 text-white/80">Ganancias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((referral, idx) => (
                        <tr key={idx} className="border-b border-purple-500/10 hover:bg-purple-500/5" data-testid={`referral-row-${idx}`}>
                          <td className="py-3 px-4 text-white">{referral.full_name}</td>
                          <td className="py-3 px-4 text-white/70">{referral.email}</td>
                          <td className="py-3 px-4 text-white/70 capitalize">{referral.user_type}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                referral.membership_status === 'active'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {referral.membership_status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-white">${referral.total_earnings.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Comisiones - Solo para Premium */}
        {activeTab === 'commissions' && !isAuthorLite && (
          <div className="space-y-6">
            {/* Payment Request Card */}
            {stats?.total_earnings >= 500 && (
              <Card className="card-viubux-dark border-green-500/30" data-testid="request-payout-card">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Solicitar Pago de Comisiones
                  </CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Los retiros solo pueden solicitarse el último viernes de cada mes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Payout Day Status */}
                  <div className={`mb-4 p-4 rounded-lg border ${payoutAvailability?.is_payout_day ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                    {payoutAvailability?.is_payout_day ? (
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-400" />
                        <p className="verdana-regular text-green-200 text-sm">
                          <span className="font-bold">¡Hoy es día de pago!</span> Puedes solicitar tu retiro.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-5 h-5 text-yellow-400" />
                          <p className="verdana-regular text-yellow-200 text-sm font-bold">
                            Próximo día de pago:
                          </p>
                        </div>
                        <p className="verdana-regular text-yellow-100 text-lg ml-7">
                          {payoutAvailability?.next_payout_date_formatted || 'Calculando...'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="verdana-regular text-white/70 text-sm mb-1">Total disponible:</p>
                      <p className="gotham-black text-3xl text-green-400">${stats.total_earnings.toFixed(2)} MXN</p>
                    </div>
                    <Button
                      data-testid="request-payout-btn"
                      onClick={handleRequestPayout}
                      className="btn-viubux"
                      disabled={!payoutAvailability?.can_request_payout}
                    >
                      {paymentRequests.some(r => r.status === 'pending') ? 'Solicitud Pendiente' : 
                       !payoutAvailability?.is_payout_day ? 'Esperar al viernes' : 'Solicitar Pago'}
                    </Button>
                  </div>
                  
                  {paymentRequests.some(r => r.status === 'pending') && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <p className="verdana-regular text-yellow-200 text-sm">
                        ⏳ Tienes una solicitud de pago pendiente. Será procesada pronto.
                      </p>
                    </div>
                  )}

                  {stats.total_earnings < (user?.user_type === 'consumer' ? 500 : 2000) && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mt-4">
                      <p className="verdana-regular text-purple-200 text-sm">
                        ℹ️ Monto mínimo para solicitar pago: ${user?.user_type === 'consumer' ? '500' : '2,000'} MXN. Te faltan ${((user?.user_type === 'consumer' ? 500 : 2000) - stats.total_earnings).toFixed(2)} MXN.
                      </p>
                    </div>
                  )}

                  {!payoutAvailability?.has_payment_method && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4">
                      <p className="verdana-regular text-red-200 text-sm">
                        ⚠️ Debes configurar tu método de pago antes de solicitar un retiro.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Invitación afiliación Nubank (NU) - siempre visible en cash-out */}
            <NubankInvite theme="dark" />

            {/* Payment Requests History */}
            {paymentRequests.length > 0 && (
              <Card className="card-viubux-dark border-purple-500/20" data-testid="payment-requests-history">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white">Historial de Solicitudes</CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Tus solicitudes de pago anteriores
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {paymentRequests.map((req, idx) => (
                      <div key={idx} className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4" data-testid={`payment-request-${idx}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="verdana-regular text-white/70 text-sm">
                              {new Date(req.created_at).toLocaleDateString('es-MX')}
                            </p>
                            <p className="gotham-bold text-white text-lg">${req.amount.toFixed(2)} MXN</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs gotham-medium ${
                            req.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {req.status === 'approved' ? 'Aprobado' : req.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="card-viubux-dark border-purple-500/20" data-testid="commissions-table">
            <CardHeader>
              <CardTitle className="gotham-bold text-white">Historial de Comisiones</CardTitle>
              <CardDescription className="verdana-regular text-white/70">
                Todas tus comisiones ganadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {commissions.length === 0 ? (
                <div className="text-center py-12 text-white/60 verdana-regular">
                  Aún no has generado comisiones
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full verdana-regular">
                    <thead>
                      <tr className="border-b border-purple-500/20">
                        <th className="text-left py-3 px-4 text-white/80">Fecha</th>
                        <th className="text-left py-3 px-4 text-white/80">Tipo</th>
                        <th className="text-left py-3 px-4 text-white/80">Monto</th>
                        <th className="text-left py-3 px-4 text-white/80">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((commission, idx) => (
                        <tr key={idx} className="border-b border-purple-500/10 hover:bg-purple-500/5" data-testid={`commission-row-${idx}`}>
                          <td className="py-3 px-4 text-white/70">
                            {new Date(commission.created_at).toLocaleDateString('es-MX')}
                          </td>
                          <td className="py-3 px-4 text-white/70 capitalize">
                            {commission.commission_type.replace('_', ' ')}
                          </td>
                          <td className="py-3 px-4 text-white gotham-bold">${commission.amount.toFixed(2)} MXN</td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                commission.payout_status === 'completed'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}
                            >
                              {commission.payout_status === 'completed' ? 'Pagado' : 'Pendiente'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Production Commissions Card (from cotizaciones.viubux.com) - Solo para Autores */}
          {user?.user_type === 'author' && (
          <Card className="card-viubux-dark border-purple-500/30 bg-gradient-to-r from-purple-900/10 to-purple-800/5" data-testid="production-commissions-card">
            <CardHeader>
              <CardTitle className="gotham-bold text-white flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-purple-400" />
                Comisiones de Producción
              </CardTitle>
              <CardDescription className="verdana-regular text-white/70">
                5% de comisión por trabajos referidos desde cotizaciones.viubux.com
              </CardDescription>
            </CardHeader>
            <CardContent>
              {productionCommissions?.count > 0 ? (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 text-center">
                      <div className="text-white/50 text-xs mb-1">Total Ganado</div>
                      <div className="gotham-bold text-lg text-purple-400">
                        {formatCurrency(productionCommissions.total_earned)}
                      </div>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-center">
                      <div className="text-white/50 text-xs mb-1">Pendiente</div>
                      <div className="gotham-bold text-lg text-yellow-400">
                        {formatCurrency(productionCommissions.pending)}
                      </div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                      <div className="text-white/50 text-xs mb-1">Pagado</div>
                      <div className="gotham-bold text-lg text-green-400">
                        {formatCurrency(productionCommissions.paid)}
                      </div>
                    </div>
                  </div>

                  {/* Commission List */}
                  <div className="overflow-x-auto">
                    <table className="w-full verdana-regular text-sm">
                      <thead>
                        <tr className="border-b border-purple-500/20">
                          <th className="text-left py-2 px-3 text-white/60">Trabajo</th>
                          <th className="text-left py-2 px-3 text-white/60">Cliente</th>
                          <th className="text-right py-2 px-3 text-white/60">Monto</th>
                          <th className="text-right py-2 px-3 text-white/60">Comisión</th>
                          <th className="text-left py-2 px-3 text-white/60">Estado</th>
                          <th className="text-left py-2 px-3 text-white/60">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productionCommissions.commissions?.slice(0, 10).map((pc, idx) => (
                          <tr key={pc.id || idx} className="border-b border-purple-500/10 hover:bg-purple-500/5">
                            <td className="py-2 px-3">
                              <div className="text-white/80 max-w-[200px] truncate">{pc.job_description}</div>
                              <div className="text-white/40 text-xs">{pc.job_id}</div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="text-white/70">{pc.client_name || '-'}</div>
                              <div className="text-white/40 text-xs">{pc.client_email}</div>
                            </td>
                            <td className="py-2 px-3 text-right text-white/60">
                              {formatCurrency(pc.transaction_amount)}
                            </td>
                            <td className="py-2 px-3 text-right text-purple-400 gotham-bold">
                              {formatCurrency(pc.commission_amount)}
                            </td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                pc.status === 'paid' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {pc.status === 'paid' ? 'Pagado' : 'Pendiente'}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-white/50">
                              {new Date(pc.created_at).toLocaleDateString('es-MX')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 mb-1">Sin comisiones de producción</p>
                  <p className="text-white/30 text-sm">Comparte tu código de referido en cotizaciones.viubux.com</p>
                </div>
              )}
            </CardContent>
          </Card>
          )}
          </div>
        )}

        {activeTab === 'subscription' && (
          <div className="space-y-6">
            {/* Subscription Status Card */}
            <Card className="card-viubux-dark border-purple-500/20" data-testid="subscription-status-card">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5" style={{ color: '#7012b2' }} />
                  Estado de Suscripción
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Gestiona tu suscripción mensual y método de pago
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Status Overview */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-black/30 rounded-lg p-4 text-center">
                      <p className="text-white/60 text-sm mb-1">Estado</p>
                      <p className={`gotham-bold text-xl ${
                        subscriptionStatus?.subscription_paused 
                          ? 'text-gray-400' 
                          : subscriptionStatus?.is_in_grace_period 
                            ? 'text-red-400'
                            : stats?.membership_status === 'active' 
                              ? 'text-green-400' 
                              : 'text-yellow-400'
                      }`}>
                        {subscriptionStatus?.subscription_paused 
                          ? 'Pausada' 
                          : subscriptionStatus?.is_in_grace_period 
                            ? 'Período de Gracia'
                            : stats?.membership_status === 'active' 
                              ? 'Activa' 
                              : 'Inactiva'}
                      </p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-4 text-center">
                      <p className="text-white/60 text-sm mb-1">Días Restantes</p>
                      <p className={`gotham-bold text-xl ${
                        subscriptionStatus?.days_remaining <= 7 ? 'text-orange-400' : 'text-white'
                      }`}>
                        {subscriptionStatus?.days_remaining ?? '--'}
                      </p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-4 text-center">
                      <p className="text-white/60 text-sm mb-1">Vence el</p>
                      <p className="gotham-bold text-xl text-white">
                        {subscriptionStatus?.subscription_expires_at 
                          ? new Date(subscriptionStatus.subscription_expires_at).toLocaleDateString('es-MX')
                          : '--'}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Botón Ver Planes - Siempre visible */}
                    <Button
                      onClick={() => user?.user_type === 'author' ? openAuthorCheckout('membership') : setShowPaymentModal(true)}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      data-testid="view-plans-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Ver Planes de Pago
                      <span className="ml-2 text-xs bg-green-500/30 px-2 py-0.5 rounded-full">20% desc. anual</span>
                    </Button>

                    {(subscriptionStatus?.show_subscription_button || subscriptionStatus?.is_in_grace_period || subscriptionStatus?.subscription_paused) && (
                      <Button
                        onClick={() => setShowPaymentModal(true)}
                        disabled={subscriptionLoading}
                        className="btn-viubux flex-1"
                        data-testid="renew-subscription-btn-tab"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Renovar Suscripción
                      </Button>
                    )}
                    
                    {subscriptionStatus?.has_active_stripe_subscription && !subscriptionStatus?.subscription_paused && (
                      <>
                        <Button
                          onClick={handlePauseSubscription}
                          variant="outline"
                          className="flex-1 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                          data-testid="pause-subscription-btn"
                        >
                          <PauseCircle className="w-4 h-4 mr-2" />
                          Pausar Suscripción
                        </Button>
                        <Button
                          onClick={handleCancelSubscription}
                          variant="outline"
                          className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
                          data-testid="cancel-subscription-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancelar Suscripción
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Switch to Free Plan */}
                  <div className="bg-black/20 border border-white/10 rounded-lg p-4" data-testid="switch-to-free-box">
                    <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                      <div>
                        <h4 className="gotham-medium text-white text-sm mb-1">¿Prefieres el plan gratuito?</h4>
                        <p className="verdana-regular text-white/60 text-xs">
                          Detén la renovación y cambia a plan gratuito. Conservas tu acceso hasta el final del período actual.
                        </p>
                      </div>
                      <Button
                        onClick={handleSwitchToFree}
                        variant="outline"
                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20 whitespace-nowrap"
                        data-testid="switch-to-free-btn"
                      >
                        Cambiar a Plan Gratis
                      </Button>
                    </div>
                  </div>

                  {/* Discount Info - Show if user has discount */}
                  {priceInfo?.discount_applied && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4" data-testid="discount-info-box">
                      <h4 className="gotham-medium text-green-400 mb-2 flex items-center gap-2">
                        <span className="text-xl">🎉</span>
                        ¡Tienes descuento por referido!
                      </h4>
                      <div className="grid md:grid-cols-3 gap-4 mb-3">
                        <div className="text-center">
                          <p className="text-white/60 text-sm">Descuento</p>
                          <p className="gotham-bold text-2xl text-green-400">{priceInfo.discount_percent}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-white/60 text-sm">Precio con descuento</p>
                          <p className="gotham-bold text-2xl text-white">${priceInfo.final_price}</p>
                          <p className="text-white/40 text-xs line-through">${priceInfo.original_price}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-white/60 text-sm">Meses restantes</p>
                          <p className="gotham-bold text-2xl text-blue-400">{priceInfo.discount_months_remaining}</p>
                        </div>
                      </div>
                      <p className="verdana-regular text-white/70 text-sm">
                        {priceInfo.message}
                      </p>
                    </div>
                  )}

                  {/* Author-specific Info Box */}
                  {user?.user_type === 'author' && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4" data-testid="author-subscription-info">
                      <h4 className="gotham-medium text-purple-300 mb-3 flex items-center gap-2">
                        <span className="text-xl">📚</span>
                        Estructura de Pagos para Autores
                      </h4>
                      <div className="space-y-3 verdana-regular text-white/80 text-sm">
                        <div className="flex items-start gap-3">
                          <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</div>
                          <div>
                            <p className="font-semibold text-white">Inscripción Inicial: $4,997 MXN (pago único)</p>
                            <p className="text-white/60 text-xs mt-1">Este pago te da acceso inmediato a la plataforma y cubre tus primeros 30 días de membresía.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</div>
                          <div>
                            <p className="font-semibold text-white">Suscripción Mensual: $199 MXN/mes (automática)</p>
                            <p className="text-white/60 text-xs mt-1">Se activa automáticamente <strong className="text-purple-300">30 días después</strong> de tu inscripción. No necesitas hacer nada, el sistema la crea por ti.</p>
                          </div>
                        </div>
                        <div className="bg-purple-900/30 rounded p-3 mt-3">
                          <p className="text-purple-200 text-xs">
                            <strong>Nota:</strong> Tu tarjeta será cobrada automáticamente cada mes a partir del día 1 del mes siguiente a tu inscripción. Recibirás un recordatorio por correo antes de cada cobro.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="gotham-medium text-white mb-2">Información General de Suscripción</h4>
                    <ul className="verdana-regular text-white/70 text-sm space-y-2">
                      <li>• <strong>Suscripción mensual:</strong> $199 MXN/mes para mantener tu membresía activa.</li>
                      <li>• <strong>Suscripción anual:</strong> $1,910.40 MXN/año (20% de descuento, ahorras $477.60).</li>
                      <li>• <strong>Período de gracia:</strong> 7 días después de vencer para renovar sin perder beneficios.</li>
                      <li>• <strong>Membresía pausada:</strong> Si no renuevas después del período de gracia, no podrás cobrar comisiones.</li>
                    </ul>
                  </div>

                  {/* Subscription paused warning */}
                  {!subscriptionStatus?.can_withdraw && stats?.membership_status === 'active' && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <p className="verdana-regular text-red-200 text-sm">
                        ⚠️ <strong>No puedes solicitar cobros</strong> mientras tu suscripción esté pausada o en período de gracia. Renueva tu suscripción para poder cobrar tus comisiones.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Canjear Comisiones por Suscripción */}
            <RedeemCommissions token={token} onSuccess={() => window.location.reload()} />
            
            {/* Configuración de Pagos */}
            <PaymentSettings token={token} userData={user} theme="dark" />

            {/* Seguridad de la Cuenta */}
            {user?.is_acting_as_owner ? (
              <Card className="card-viubux-dark border-purple-500/20" data-testid="assistant-security-notice">
                <CardContent className="p-5 text-white/70 text-sm">
                  🔒 Estás administrando la cuenta de <strong className="text-white">{user?.managing_owner_name}</strong> como asistente/editor.
                  Por seguridad, no puedes ver ni modificar el usuario, correo, datos bancarios, PIN ni los pagos del autor.
                </CardContent>
              </Card>
            ) : (
              <AccountSecurity />
            )}
          </div>
        )}

        {/* Benefits Tab - Only for Authors */}
        {activeTab === 'benefits' && user?.user_type === 'author' && (
          <div className="space-y-6">
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader>
                <CardTitle className="gotham-bold text-2xl text-white flex items-center gap-3">
                  🎁 Beneficios de tu Membresía ViubuxConnect®
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Como autor con membresía activa ($4,997 MXN inscripción), tienes acceso a estos beneficios exclusivos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* BENEFIT 1 - Links de Referido */}
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/10 p-6 rounded-xl border border-green-500/30">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">🔗</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">1. Obtén tu enlace Único y Sube tu PI</h3>
                      <p className="verdana-regular text-white/70 mb-4">
                        Sube tu PI (<strong className="text-green-400">hasta 2GB de Hosting</strong> para tus libros, ebooks, audiolibros, podcast, curso, conferencias, booktrailer, etc.) y forma parte del Catálogo de Suscripción de Viubux Connect y <strong className="text-green-400">comienza a monetizar HOY mismo</strong>. Comparte tu enlace único para invitar nuevos miembros y ganar <strong className="text-green-400">comisiones del 25%</strong>.
                      </p>
                      
                      {/* Link CON descuento - Username */}
                      {user?.username && (
                        <div className="mb-4 p-4 bg-black/30 rounded-lg border border-green-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="gotham-medium text-green-400 text-sm">Con 20% Descuento (Username)</span>
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Recomendado</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <code className="verdana-regular text-sm text-green-300 break-all flex-1" data-testid="referral-link-username">
                              https://connect.viubux.com?ref={user?.username}
                            </code>
                            <Button
                              data-testid="copy-referral-link-username"
                              onClick={() => {
                                navigator.clipboard.writeText(`https://connect.viubux.com?ref=${user?.username}`);
                                toast.success('Link con username copiado');
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copiar
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Link CON descuento - Código */}
                      <div className="mb-4 p-4 bg-black/30 rounded-lg border border-green-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 rounded-full bg-green-500/60"></div>
                          <span className="gotham-medium text-green-400/80 text-sm">Con 20% Descuento (Código)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <code className="verdana-regular text-sm text-green-300/80 break-all flex-1" data-testid="referral-link-discount">
                            https://connect.viubux.com?ref={user?.referral_code}
                          </code>
                          <Button
                            data-testid="copy-referral-link-discount"
                            onClick={() => {
                              navigator.clipboard.writeText(`https://connect.viubux.com?ref=${user?.referral_code}`);
                              toast.success('Link con código copiado');
                            }}
                            className="bg-green-600/80 hover:bg-green-700 text-white"
                            size="sm"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copiar
                          </Button>
                        </div>
                      </div>
                      
                      {/* Link SIN descuento */}
                      <div className="p-4 bg-black/30 rounded-lg border border-purple-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                          <span className="gotham-medium text-purple-400 text-sm">Sin Descuento</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <code className="verdana-regular text-sm text-purple-300 break-all flex-1" data-testid="referral-link-no-discount">
                            https://connect.viubux.com?ref={user?.username || user?.referral_code}&nd=1
                          </code>
                          <Button
                            data-testid="copy-referral-link-no-discount"
                            onClick={() => {
                              navigator.clipboard.writeText(`https://connect.viubux.com?ref=${user?.username || user?.referral_code}&nd=1`);
                              toast.success('Link sin descuento copiado');
                            }}
                            className="btn-viubux"
                            size="sm"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copiar
                          </Button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button 
                          onClick={handleDistributionRequest}
                          disabled={sendingDistributionEmail}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white gotham-bold"
                        >
                          {sendingDistributionEmail ? '⏳ Enviando...' : '🚀 Subir mi PI / Iniciar Distribución'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 2 - Comunidad VIP + Pool 42% */}
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/10 p-6 rounded-xl border border-amber-500/30">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">⭐</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">2. Comunidad VIP + Pool 42%</h3>
                      <p className="verdana-regular text-white/70">
                        Únete a la comunidad VIP de VC y ten acceso inmediato al <strong className="text-amber-400">Pool de Autores del 42%</strong> (adicional al <strong className="text-amber-400">25% del rapid bonus</strong>).
                      </p>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 3 - Co-Producciones */}
                <div className="bg-gradient-to-r from-emerald-500/10 to-transparent p-6 rounded-xl border border-emerald-500/20">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">🤲</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">3. Hasta 50% en Co-Producciones</h3>
                      <p className="verdana-regular text-white/70 mb-4">
                        Obtén hasta <strong className="text-emerald-400">50% de apoyo en Co-Producciones</strong> + un <strong className="text-emerald-400">15% de Descuento</strong> en todos los servicios que Viubux Media ofrece para ti.
                      </p>
                      <a 
                        href="https://Cotizaciones.Viubux.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button className="bg-emerald-500 hover:bg-emerald-600 text-white gotham-bold">
                          💼 Conocer Más
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 4 - Catálogo Viubux Connect */}
                <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/10 p-6 rounded-xl border border-indigo-500/30">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">🎧</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">4. Catálogo Viubux Connect</h3>
                      <p className="verdana-regular text-white/70 mb-4">
                        Acceso al catálogo de Viubux Connect en <strong className="text-indigo-400">Viubux Reader y Viubux Play</strong>. Cualquier título exento de la colección de ViubuxConnect no forma parte de la suscripción y requerirá ser adquirido de manera adicional.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <a 
                          href="https://app.viubux.com/library/filter?collection=VIUBUXCONNECT&orderBy=sales" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button className="bg-indigo-500 hover:bg-indigo-600 text-white gotham-bold">
                            🎧 Ir a Viubux Reader
                          </Button>
                        </a>
                        <a 
                          href="https://play.viubux.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button className="bg-rose-500 hover:bg-rose-600 text-white gotham-bold">
                            ▶️ Ir a Viubux Play
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 5 - Tu Canal en Viubux Play */}
                <div className="bg-gradient-to-r from-pink-500/10 to-transparent p-6 rounded-xl border border-pink-500/20">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">📺</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">5. Tu Canal en Viubux Play</h3>
                      <p className="verdana-regular text-white/70 mb-4">
                        Recibe <strong className="text-pink-400">soporte técnico</strong> para actualizarlo, subir tu contenido y personalizarlo.
                      </p>
                      <a 
                        href="https://Cotizaciones.Viubux.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button className="bg-pink-500 hover:bg-pink-600 text-white gotham-bold">
                          📺 Solicitar Soporte
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 6 - Descuentos Exclusivos */}
                <div className="bg-gradient-to-r from-green-500/10 to-transparent p-6 rounded-xl border border-green-500/20">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">💰</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">6. Descuentos Exclusivos</h3>
                      <p className="verdana-regular text-white/70">
                        En <strong className="text-green-400">contenido de Alto Valor Transformacional</strong> y servicios ofrecidos por miembros de la comunidad. Además, <strong className="text-green-400">15% de descuento</strong> en servicios ViubuxMedia®:
                      </p>
                      <ul className="mt-3 space-y-1 verdana-regular text-white/60 text-sm">
                        <li>• Booktrailers y Podcast</li>
                        <li>• Producción de Audiolibros</li>
                        <li>• Servicios de Edición y Diseño</li>
                        <li>• Photoshoot Profesional</li>
                        <li>• NFC Cards</li>
                        <li>• Y mucho más...</li>
                      </ul>
                      <a 
                        href="https://Cotizaciones.Viubux.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block mt-4"
                      >
                        <Button className="bg-green-500 hover:bg-green-600 text-white">
                          Cotiza tu siguiente producción →
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 7 - Networking Privado */}
                <div className="bg-gradient-to-r from-yellow-500/10 to-transparent p-6 rounded-xl border border-yellow-500/20">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">🤝</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">7. Networking Privado</h3>
                      <p className="verdana-regular text-white/70 mb-3">
                        Con <strong className="text-yellow-400">Socios Estratégicos</strong> de la comunidad.
                      </p>
                      <a 
                        href="https://connect.viubux.com/networking" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button className="bg-yellow-500 hover:bg-yellow-600 text-black gotham-bold">
                          Ir a Community Networking
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 8 - LiveStream + Eventos */}
                <div className="bg-gradient-to-r from-blue-500/10 to-transparent p-6 rounded-xl border border-blue-500/20">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">🎥</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">8. LiveStream + Eventos</h3>
                      <p className="verdana-regular text-white/70">
                        <strong className="text-blue-400">LiveStream</strong> + publica tus eventos ante la comunidad.
                      </p>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 9 - Suite Pro-Marketing */}
                <div className="bg-gradient-to-r from-fuchsia-500/20 to-purple-500/10 p-6 rounded-xl border border-fuchsia-500/30">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">🚀</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">9. Suite Pro-Marketing</h3>
                      <p className="verdana-regular text-white/70 mb-4">
                        Acceso a la <strong className="text-fuchsia-400">Suite de Herramientas tecnológicas: Pro-Marketing</strong>:
                      </p>
                      <ul className="space-y-2 verdana-regular text-white/60 text-sm mb-4">
                        <li className="flex items-center gap-2">
                          <span className="text-fuchsia-400">✓</span> Creación de Enlace Único (tipo LinkTree)
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-fuchsia-400">✓</span> Descarga de QR personalizado
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-fuchsia-400">✓</span> Booktrailer automatizado con IA
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-fuchsia-400">✓</span> Separadores de libros con QR
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-fuchsia-400">✓</span> Tarjetas de presentación profesionales
                        </li>
                      </ul>
                      <Button 
                        onClick={() => setActiveTab('marketing')}
                        className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white gotham-bold"
                      >
                        🚀 Ir a Pro-Marketing
                      </Button>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 10 - 1 Evento Privado al Año */}
                <div className="bg-gradient-to-r from-purple-500/10 to-transparent p-6 rounded-xl border border-purple-500/20">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">🎤</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">10. 1 Evento Privado al Año</h3>
                      <p className="verdana-regular text-white/70 mb-4">
                        Patrocinio de un <strong className="text-purple-400">Business Room o MiniForo</strong> (máx. 20 personas) con Audio y Video + grabación de tu ponencia en tu canal de Viubux Play.
                      </p>
                      <Button 
                        onClick={() => setShowEventModal(true)}
                        className="bg-purple-500 hover:bg-purple-600 text-white gotham-bold"
                      >
                        🎫 Ocupar Bono
                      </Button>
                    </div>
                  </div>
                </div>

                {/* BENEFIT 11 - PhotoShoot + 3 Stories */}
                <div className="bg-gradient-to-r from-rose-500/10 to-transparent p-6 rounded-xl border border-rose-500/20">
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">📸</span>
                    <div className="flex-1">
                      <h3 className="gotham-bold text-xl text-white mb-2">11. PhotoShoot + 3 Stories</h3>
                      <p className="verdana-regular text-white/70">
                        Para dar a conocer <strong className="text-rose-400">tus obras y tu canal de Viubux Play</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bonos y Beneficios adicionales para autores destacados */}
                <div className="bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-transparent p-6 rounded-xl border border-amber-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🏆</span>
                    <h3 className="gotham-bold text-xl text-white">Bonos y Beneficios adicionales para <span className="text-amber-300">autores destacados</span></h3>
                  </div>
                  <ul className="space-y-2 verdana-regular text-white/70 text-sm">
                    <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Entrevistas en medios y podcasts (ADN40, Radio Disney, etc.)</li>
                    <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Participa como Speaker en la convención anual de VC del siguiente año y tu libro destacado en el stand de la FIL de Guadalajara</li>
                    <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Programa Piloto para tu Podcast o Curso</li>
                  </ul>
                  <p className="text-white/45 text-xs mt-4 italic">
                    * Autor destacado es aquel que genera al menos <strong className="text-amber-300/80">20 suscriptores</strong> en su canal de Viubux Connect durante <strong className="text-amber-300/80">3 meses consecutivos</strong>.
                  </p>
                </div>

                {/* CTA */}
                <div className="text-center pt-4 border-t border-white/10">
                  <p className="verdana-regular text-white/60 mb-4">
                    ¿Tienes preguntas sobre tus beneficios?
                  </p>
                  <a 
                    href="https://connect.viubux.com/soporte" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="border-purple-500/50 text-white hover:bg-purple-500/20">
                      Contactar Soporte
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Simulator Tab - Only for Author Premium */}
        {activeTab === 'simulator' && user?.user_type === 'author' && !isAuthorLite && (
          <div className="space-y-6">
            {/* Community Metrics - Conditionally visible */}
            <CommunityMetrics stats={communityStats} isVisible={metricsVisible} />
            
            {/* Earnings Simulator */}
            <EarningsSimulator communityStats={communityStats} user={user} />
            
            {/* Info Card */}
            <Card className="card-viubux-dark border-blue-500/30">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/20 p-3 rounded-lg">
                    <Calculator className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg text-white mb-2">¿Cómo funciona el simulador?</h3>
                    <ul className="space-y-2 text-white/70 text-sm verdana-regular">
                      <li>• <strong className="text-green-400">Bono Rápido (25%):</strong> Ganas $49.75 MXN por cada referido activo que mantengas</li>
                      <li>• <strong className="text-purple-400">Pool de Autores (42%):</strong> El 42% de TODOS los ingresos se divide entre los autores activos</li>
                      <li>• <strong className="text-yellow-400">Multiplicador de Referidos:</strong> 0 ref sin susc = 5% • 0 ref activo = 10% • 1 ref = 25% • 2 ref = 50% • 3 ref = 75% • 4+ ref = 100%</li>
                      <li>• <strong className="text-white">A más suscriptores = más valor</strong> del pool para todos los autores</li>
                      <li>• Los valores se actualizan automáticamente con datos reales de la comunidad</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'marketing' && (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6">
            <ProMarketing user={user} token={token} />
          </div>
        )}
        {activeTab === 'gallery' && (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6">
            <MyImageGallery token={token} />
          </div>
        )}
      </div>

      {/* Payment Plan Modal */}
      <PaymentPlanModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        userType={user?.user_type}
        onPaymentSuccess={() => {
          setShowPaymentModal(false);
          fetchDashboardData();
        }}
      />

      {/* Author Checkout Modal (Membresía + Suscripción) */}
      <AuthorCheckoutModal
        isOpen={showAuthorCheckout}
        onClose={() => setShowAuthorCheckout(false)}
        initialMembership={authorInitial.membership}
        initialSubscription={authorInitial.subscription}
        onSuccess={() => {
          setShowAuthorCheckout(false);
          fetchDashboardData();
          fetchAuthorOptions();
        }}
      />

      {/* Event Request Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] border border-purple-500/30 rounded-2xl max-w-md w-full p-6 relative">
            <button 
              onClick={() => setShowEventModal(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="gotham-bold text-xl text-white mb-2 flex items-center gap-2">
              🎫 Ocupar Bono de Evento
            </h3>
            <p className="text-white/60 text-sm mb-6">
              Reserva la fecha de tu evento anual y procede a cotizar el evento completo.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Fecha Preferida del Evento *</label>
                <input
                  type="date"
                  value={eventFormData.date}
                  onChange={(e) => setEventFormData({...eventFormData, date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-2">Descripción del Evento (opcional)</label>
                <textarea
                  value={eventFormData.description}
                  onChange={(e) => setEventFormData({...eventFormData, description: e.target.value})}
                  rows={3}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 resize-none"
                  placeholder="Ej: Lanzamiento de mi libro 'Título', espero 50 asistentes..."
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => setShowEventModal(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleEventRequest}
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white gotham-bold"
                >
                  Reservar y Cotizar →
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App.Viubux Uploader Modal */}
      {showPublicalaUploader && (
        <PublicalaUploader 
          user={user}
          onClose={() => setShowPublicalaUploader(false)}
        />
      )}

      {/* Viubux Play Uploader Modal */}
      <ViubuxPlayUploader 
        isOpen={showViubuxPlayUploader}
        onClose={() => setShowViubuxPlayUploader(false)}
        user={user}
        token={token}
      />
      
      {/* AI Chat Widget */}
      <AIChatWidget token={token} user={user} />
    </div>
  );
};

export default Dashboard;